import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Pin, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ATALHOS } from '@/app/shell/atalhos';
import { useTabs } from './TabsProvider';

const TabBar: React.FC = () => {
  const { activeKey, fecharAba, tabs } = useTabs();
  const barraRef = useRef<HTMLDivElement | null>(null);

  // A aba ativa entra na tela sozinha quando está fora dela (ex.: aberta pelo menu com muitas abas).
  useEffect(() => {
    barraRef.current
      ?.querySelector<HTMLElement>('[data-ativa="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeKey, tabs.length]);

  if (tabs.length === 0) return null;

  const handleClose = (event: React.MouseEvent, key: string) => {
    event.preventDefault();
    event.stopPropagation();
    fecharAba(key);
  };

  return (
    // No celular a barra de abas ocupa uma linha e quase não dá para usar; o menu e a trilha do cabeçalho bastam.
    <div className="hidden border-b border-[var(--app-hairline)] bg-[var(--brand-charcoal)] md:block">
      <div
        ref={barraRef}
        className="barra-rolagem-escura barra-abas flex h-11 items-start gap-1 overflow-x-auto overflow-y-hidden px-3 pt-2"
      >
        {tabs.map((tab) => {
          const active = tab.key === activeKey;
          return (
            <Link
              key={tab.key}
              to={tab.path}
              data-ativa={active}
              className={cn(
                'group flex h-9 min-w-[148px] max-w-[260px] items-center gap-2 rounded-t-md border px-3 text-sm transition-colors',
                active
                  ? 'border-[var(--brand-hairline)] border-b-white bg-white text-[#34343A]'
                  : 'border-transparent bg-[var(--brand-charcoal-soft)] text-[var(--brand-ink-2)] hover:bg-[#45454B] hover:text-white',
              )}
              title={`${tab.section} / ${tab.title}${tab.dirty ? ' · alteração não salva' : ''}`}
              // Botão do meio fecha a aba, como no navegador (e não abre o link numa aba nova do navegador).
              onMouseDown={(event) => {
                if (event.button === 1) event.preventDefault();
              }}
              onAuxClick={(event) => {
                if (event.button !== 1 || tab.closable === false || tab.pinned) return;
                handleClose(event, tab.key);
              }}
            >
              {tab.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-current opacity-70" />}
              <span className="min-w-0 flex-1 truncate">{tab.title}</span>
              {tab.dirty && <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-label="Alteração não salva" />}
              {tab.closable !== false && !tab.pinned && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0 rounded-sm opacity-60 hover:bg-black/10 hover:opacity-100"
                  onClick={(event) => handleClose(event, tab.key)}
                  aria-label={`Fechar ${tab.title}`}
                  title={active ? `Fechar (${ATALHOS.fecharAba})` : 'Fechar'}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TabBar;
